import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import { productionApi, repairsApi } from '../api/production';
import designsApi from '../api/designs';
import { Design } from '../api/design';
import instance from '../utils/axios';

interface SizeData {
  qc_qty: number;
  in_repair_qty: number;
  damaged_qty: number;
  barcoded_qty: number;
}

export default function OpeningEntryForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Design selection
  const [selectedDesign, setSelectedDesign] = useState<Design | null>(null);
  const [designs, setDesigns] = useState<Design[]>([]);
  const [designsLoading, setDesignsLoading] = useState(false);
  const [designNo, setDesignNo] = useState('');

  // Size-wise data: { size: { qc_qty, in_repair_qty, damaged_qty, barcoded_qty } }
  const [sizeData, setSizeData] = useState<{ [size: string]: SizeData }>({});

  // Repair types (for repair items if needed)
  const [repairTypes, setRepairTypes] = useState<any[]>([]);
  const [selectedRepairType, setSelectedRepairType] = useState<number | ''>('');

  // Bulk upload state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState<any>(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);

  // ERP Comparison state
  const [comparisonCsvFile, setComparisonCsvFile] = useState<File | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonResult, setComparisonResult] = useState<any>(null);
  const [showMismatchOnly, setShowMismatchOnly] = useState(false);

  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  // Initialize size data
  useEffect(() => {
    const initialData: { [size: string]: SizeData } = {};
    sizes.forEach(size => {
      initialData[size] = {
        qc_qty: 0,
        in_repair_qty: 0,
        damaged_qty: 0,
        barcoded_qty: 0
      };
    });
    setSizeData(initialData);
  }, []);

  // Fetch designs on mount
  useEffect(() => {
    fetchDesigns();
    fetchRepairTypes();
  }, []);

  const fetchDesigns = async () => {
    try {
      setDesignsLoading(true);
      const pageSize = 200;
      let page = 1;
      let allDesigns: Design[] = [];
      
      while (true) {
        const response = await instance.get('/designs', {
          params: { summary: 1, page, pageSize, withCount: 1 }
        });
        
        const data = Array.isArray(response.data) ? response.data : [];
        allDesigns = allDesigns.concat(data);
        
        const total = Number(response.headers['x-total-count'] || 0);
        if (total > 0) {
          if (page * pageSize >= total) break;
        } else {
          if (data.length < pageSize) break;
        }
        page += 1;
      }
      
      setDesigns(allDesigns);
    } catch (err: any) {
      console.error('Error fetching designs:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load designs';
      setError(`Failed to load designs: ${errorMsg}`);
    } finally {
      setDesignsLoading(false);
    }
  };

  const fetchRepairTypes = async () => {
    try {
      const response = await repairsApi.getTypes();
      setRepairTypes(response);
    } catch (err: any) {
      console.error('Error fetching repair types:', err);
    }
  };

  const handleSizeDataChange = (size: string, field: keyof SizeData, value: string) => {
    const numValue = parseInt(value) || 0;
    setSizeData(prev => ({
        ...prev,
        [size]: {
          ...prev[size],
        [field]: Math.max(0, numValue)
        }
      }));
  };

  const getSizeTotal = (size: string): number => {
    const data = sizeData[size];
    if (!data) return 0;
    return data.qc_qty + data.in_repair_qty + data.damaged_qty + data.barcoded_qty;
  };

  const getFieldTotal = (field: keyof SizeData): number => {
    return Object.values(sizeData).reduce((sum, data) => sum + (data[field] || 0), 0);
  };

  const getGrandTotal = (): number => {
    return Object.values(sizeData).reduce((sum, data) => 
      sum + data.qc_qty + data.in_repair_qty + data.damaged_qty + data.barcoded_qty, 0
    );
  };

  const handleSubmit = async () => {
    try {
      if (!selectedDesign) {
        setError('Please select a design');
        return;
      }

      const grandTotal = getGrandTotal();
      if (grandTotal === 0) {
        setError('Please enter at least some quantity');
        return;
      }

      setLoading(true);

      // Calculate totals
      const qcTotal = getFieldTotal('qc_qty');
      const repairTotal = getFieldTotal('in_repair_qty');
      const damagedTotal = getFieldTotal('damaged_qty');
      const barcodedTotal = getFieldTotal('barcoded_qty');
        
      // Check if repair quantities exist but no repair type selected
      if (repairTotal > 0 && !selectedRepairType) {
        setError('Please select a repair type when entering repair quantities');
        setLoading(false);
        return;
      }
        
      // Build size breakdown
      const sizeBreakdown = sizes
        .map(size => {
          const data = sizeData[size];
          return {
            size,
            qc_qty: data?.qc_qty || 0,
            in_repair_qty: data?.in_repair_qty || 0,
            damaged_qty: data?.damaged_qty || 0,
            barcoded_qty: data?.barcoded_qty || 0,
            cutting_qty: 0,
            stitching_qty: 0,
            dhaga_qty: 0,
            press_qty: 0,
            issued_qty: getSizeTotal(size)
          };
        })
        .filter(sb => getSizeTotal(sb.size) > 0);

      // Determine current stage based on where most pieces are

      let currentStage = 'QC';
      if (barcodedTotal > qcTotal && barcodedTotal > repairTotal) {
        currentStage = 'COMPLETED';
      } else if (repairTotal > qcTotal) {
        currentStage = 'QC'; // Still at QC stage even if some in repair
      }

        const payload: any = {
          design_no: selectedDesign.design_no,
        total_issued: grandTotal,
          current_stage: currentStage,
          size_breakdown: sizeBreakdown,
          is_opening_entry: true
        };

      // If there are repair items, add them
      if (repairTotal > 0 && selectedRepairType) {
          payload.repair_items = sizes
          .filter(size => sizeData[size]?.in_repair_qty > 0)
            .map(size => ({
              size,
            quantity: sizeData[size].in_repair_qty,
              repair_type_id: selectedRepairType
            }));
        }

        await productionApi.createOpeningEntry(payload);

      setSuccess('Opening entry created successfully!');
      setTimeout(() => navigate('/production/qc'), 2000);
    } catch (err: any) {
      console.error('Error creating opening entry:', err);
      
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const errorMessages = err.response.data.errors
          .map((e: any) => e.msg || e.message)
          .join(', ');
        setError(`Validation error: ${errorMessages}`);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to create opening entry. Please check all fields and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setBulkLoading(true);
      setError(null);
      
      const repairTypeId = selectedRepairType ? Number(selectedRepairType) : undefined;
      const result = await productionApi.bulkOpeningEntry(csvFile, repairTypeId);
      
      setBulkResults(result);
      setResultsDialogOpen(true);
      
      if (result.processed > 0) {
        setSuccess(`Successfully processed ${result.processed} designs!`);
      }
      
      if (result.unmatched > 0) {
        setError(`${result.unmatched} rows could not be matched. Check the results for details.`);
      }
    } catch (err: any) {
      console.error('Error processing bulk upload:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process bulk upload');
    } finally {
      setBulkLoading(false);
    }
  };

  const downloadUnmatchedCSV = () => {
    if (!bulkResults || !bulkResults.unmatched_rows || bulkResults.unmatched_rows.length === 0) {
      return;
    }

    // Get all unique keys from all rows
    const allKeys = new Set<string>();
    bulkResults.unmatched_rows.forEach((row: any) => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });

    const headers = Array.from(allKeys);
    const csvRows = bulkResults.unmatched_rows.map((row: any) => {
      return headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      });
    });

    const csvContent = [
      headers.join(','),
      ...csvRows.map((row: string[]) => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unmatched_opening_entries_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadSampleCSV = () => {
    // Sample CSV with headers and dummy data
    const headers = ['Article Name', 'Size', 'QC', 'Repair', 'Damaged', 'Barcoded', 'Repair Type', 'Quantity'];
    const sampleRow = [
      'AARD-2989 - BLUE',  // Article Name - will extract "2989"
      'M',                  // Size
      '10',                 // QC Pending
      '2',                  // Repair
      '1',                  // Damaged
      '5',                  // Barcoded
      'Button Issue',        // Repair Type (must match system repair types)
      '18'                  // Quantity (optional - sum of above)
    ];

    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_opening_entry_bulk_upload.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ERP Comparison handlers
  const handleCompare = async () => {
    if (!comparisonCsvFile) {
      setError('Please select a CSV file');
      return;
    }
    try {
      setComparisonLoading(true);
      setError(null);
      setComparisonResult(null);
      const result = await productionApi.compareWithERP(comparisonCsvFile);
      setComparisonResult(result);
      if (result.unmatched > 0) {
        setError(`${result.unmatched} rows could not be matched to system designs.`);
      }
      if (result.comparison?.length > 0 && result.summary?.mismatched_rows === 0) {
        setSuccess('All rows match! No variance found.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to compare with ERP');
    } finally {
      setComparisonLoading(false);
    }
  };

  const downloadComparisonSampleCSV = () => {
    const headers = ['Article Name', 'Size', 'Pending Qty', 'Closing Qty'];
    const sampleRows = [
      ['AARD-2989 - BLUE', 'M', '10', '5'],
      ['AARD-2989 - BLUE', 'L', '8', '3'],
      ['AARD-3150 - RED', 'S', '15', '10']
    ];
    const csvContent = [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_erp_comparison.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadComparisonCSV = () => {
    if (!comparisonResult?.comparison?.length) return;
    const headers = ['Article Name', 'Design No', 'Size', 'ERP Qty', 'QC Pending', 'Press Pending', 'Repair', 'System Total', 'Variance', 'Mismatch'];
    const rows = comparisonResult.comparison.map((r: any) => [
      `"${(r.article_name || '').replace(/"/g, '""')}"`,
      r.design_no, r.size, r.erp_qty,
      r.system_qc_pending, r.system_press_pending, r.system_repair_pending, r.system_total_pending, r.variance,
      r.has_mismatch ? 'YES' : 'NO'
    ]);
    const csvContent = [headers.join(','), ...rows.map((r: any[]) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `erp_comparison_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadComparisonUnmatchedCSV = () => {
    if (!comparisonResult?.unmatched_rows?.length) return;
    const headers = ['Article Name', 'Size', 'Extracted Design No', 'Reason'];
    const rows = comparisonResult.unmatched_rows.map((r: any) => [
      `"${(r.article_name || '').replace(/"/g, '""')}"`,
      r.size || '', r.extracted_design_no || '', `"${(r.reason || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = [headers.join(','), ...rows.map((r: any[]) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `erp_unmatched_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/production/qc')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5">Opening Entry</Typography>
        </Box>

        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Single Entry" />
          <Tab label="Bulk Upload (CSV)" />
          <Tab label="ERP Comparison" />
        </Tabs>

        <Alert severity="info" sx={{ mb: 3 }}>
          {currentTab === 0 ? (
            <>
              Enter the current status of pieces for this design. You can enter quantities for:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li><strong>QC Pending:</strong> Pieces currently at QC stage</li>
                <li><strong>In Repair:</strong> Pieces sent for repair</li>
                <li><strong>Damaged:</strong> Permanently damaged pieces</li>
                <li><strong>Barcoded:</strong> Pieces that have been barcoded (completed)</li>
              </ul>
            </>
          ) : currentTab === 1 ? (
            <>
              Upload a CSV file with columns: <strong>Article Name</strong>, <strong>Size</strong>, <strong>QC</strong>, <strong>Repair</strong>, <strong>Damaged</strong>, <strong>Barcoded</strong>, <strong>Repair Type</strong> (optional), <strong>Quantity</strong> (optional).
              <br />
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li><strong>Article Name:</strong> The system will extract the 4-digit design number (e.g., "AARD-2989" or "AARD - 2989 - BLUE" → "2989")</li>
                <li><strong>Repair Type:</strong> Must match exactly (case-insensitive, trimmed) with repair types in the system. Required if Repair quantity &gt; 0</li>
                <li><strong>Quantity:</strong> Optional total quantity. If not provided, will be calculated as sum of QC + Repair + Damaged + Barcoded</li>
              </ul>
            </>
          ) : (
            <>
              Upload your ERP stock report (CSV or Excel). The system auto-detects columns like <strong>article_name</strong>, <strong>category3/Size</strong>, <strong>closingstockqty</strong>, <strong>pendingstockqty</strong>.
              <br />
              Design numbers are extracted from article names (e.g. "AARD-2989-BLUE" → 2989) and matched with system designs. Title rows and totals are skipped automatically.
            </>
          )}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Single Entry Form */}
        {currentTab === 0 && (
          <>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={designs}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.design_no || ''}
              value={selectedDesign}
              onChange={(event, newValue) => {
                setSelectedDesign(newValue as Design | null);
                setDesignNo(newValue?.design_no || '');
              }}
              inputValue={designNo}
              onInputChange={(event, newInputValue) => {
                setDesignNo(newInputValue);
              }}
              loading={designsLoading}
              renderOption={(props, option) => {
                const design = option as Design;
                const imageUrl = design.image_url || design.image2_url || design.image3_url || design.image4_url;
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt={design.design_no}
                        style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 4 }}
                      />
                    )}
                    <Typography>{design.design_no}</Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Design"
                  placeholder="Type to search..."
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {designsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {getFieldTotal('in_repair_qty') > 0 && (
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={repairTypes}
                getOptionLabel={(option) => option.name || ''}
                value={repairTypes.find(t => t.id === selectedRepairType) || null}
                onChange={(event, newValue) => {
                  setSelectedRepairType(newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                  label="Repair Type *"
                    required
                    helperText="Required when entering repair quantities"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        {selectedDesign && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Enter Quantities by Size
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Size</strong></TableCell>
                      <TableCell align="right"><strong>QC Pending</strong></TableCell>
                      <TableCell align="right"><strong>In Repair</strong></TableCell>
                      <TableCell align="right"><strong>Damaged</strong></TableCell>
                      <TableCell align="right"><strong>Barcoded</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sizes.map(size => {
                      const data = sizeData[size] || { qc_qty: 0, in_repair_qty: 0, damaged_qty: 0, barcoded_qty: 0 };
                      const total = getSizeTotal(size);
                      
                      return (
                        <TableRow key={size}>
                          <TableCell><strong>{size}</strong></TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={data.qc_qty}
                              onChange={(e) => handleSizeDataChange(size, 'qc_qty', e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={data.in_repair_qty}
                              onChange={(e) => handleSizeDataChange(size, 'in_repair_qty', e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={data.damaged_qty}
                              onChange={(e) => handleSizeDataChange(size, 'damaged_qty', e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={data.barcoded_qty}
                              onChange={(e) => handleSizeDataChange(size, 'barcoded_qty', e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={total} 
                              color={total > 0 ? 'primary' : 'default'} 
                              size="small" 
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><strong>TOTAL</strong></TableCell>
                      <TableCell align="right">
                        <strong>{getFieldTotal('qc_qty')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{getFieldTotal('in_repair_qty')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{getFieldTotal('damaged_qty')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{getFieldTotal('barcoded_qty')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={getGrandTotal()} 
                          color="primary" 
                          size="medium"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
            onClick={() => navigate('/production/qc')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
            disabled={loading || !selectedDesign || getGrandTotal() === 0}
              >
            {loading ? <CircularProgress size={24} /> : 'Create Opening Entry'}
              </Button>
            </Box>
          </>
        )}

        {/* Bulk Upload Form */}
        {currentTab === 1 && (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadSampleCSV}
                size="small"
              >
                Download Sample CSV
              </Button>
            </Box>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {csvFile ? csvFile.name : 'Select CSV File'}
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCsvFile(file);
                      }
                    }}
                  />
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={repairTypes}
                  getOptionLabel={(option) => option.name || ''}
                  value={repairTypes.find(t => t.id === selectedRepairType) || null}
                  onChange={(event, newValue) => {
                    setSelectedRepairType(newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Repair Type (Optional)"
                      helperText="Fallback if CSV doesn't have Repair Type column or if repair type in CSV doesn't match"
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/production/qc')}
                disabled={bulkLoading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleBulkUpload}
                disabled={bulkLoading || !csvFile}
                startIcon={bulkLoading ? <CircularProgress size={20} /> : <UploadFileIcon />}
              >
                {bulkLoading ? 'Processing...' : 'Upload & Process CSV'}
              </Button>
            </Box>
          </Box>
        )}

        {/* ERP Comparison Tab */}
        {currentTab === 2 && (
          <Box>
            {/* Upload section */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadComparisonSampleCSV} size="small">
                Download Sample CSV
              </Button>
            </Box>
            <Grid container spacing={3} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {comparisonCsvFile ? comparisonCsvFile.name : 'Select ERP File (CSV / Excel)'}
                  <input
                    type="file"
                    hidden
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) { setComparisonCsvFile(file); setComparisonResult(null); }
                    }}
                  />
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleCompare}
                    disabled={comparisonLoading || !comparisonCsvFile}
                    startIcon={comparisonLoading ? <CircularProgress size={20} /> : undefined}
                  >
                    {comparisonLoading ? 'Comparing...' : 'Compare with System'}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Comparison Results */}
            {comparisonResult && (
              <>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary">Matched</Typography>
                        <Typography variant="h5" fontWeight={700}>{comparisonResult.summary.matched_designs}</Typography>
                        <Typography variant="caption" color="text.secondary">{comparisonResult.summary.total_rows} rows</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card variant="outlined" sx={{ borderColor: comparisonResult.summary.variance_total !== 0 ? 'error.main' : 'success.main' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary">ERP Qty vs System Pending</Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {comparisonResult.summary.total_erp_qty} vs {comparisonResult.summary.total_system_pending}
                        </Typography>
                        <Typography variant="caption" fontWeight={600} color={comparisonResult.summary.variance_total !== 0 ? 'error.main' : 'success.main'}>
                          Δ {comparisonResult.summary.variance_total > 0 ? '+' : ''}{comparisonResult.summary.variance_total}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card variant="outlined" sx={{ borderColor: comparisonResult.summary.mismatched_rows > 0 ? 'error.main' : 'success.main' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="text.secondary">Mismatched Rows</Typography>
                        <Typography variant="h5" fontWeight={700} color={comparisonResult.summary.mismatched_rows > 0 ? 'error.main' : 'success.main'}>
                          {comparisonResult.summary.mismatched_rows}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">of {comparisonResult.summary.total_rows}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Show mismatches only"
                      onClick={() => setShowMismatchOnly(!showMismatchOnly)}
                      color={showMismatchOnly ? 'error' : 'default'}
                      variant={showMismatchOnly ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </Box>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadComparisonCSV} size="small">
                    Download Comparison CSV
                  </Button>
                </Box>

                {/* Comparison Table */}
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Article Name</strong></TableCell>
                        <TableCell><strong>Design No</strong></TableCell>
                        <TableCell><strong>Size</strong></TableCell>
                        <TableCell align="right"><strong>ERP Qty</strong></TableCell>
                        <TableCell align="right"><strong>QC Pending</strong></TableCell>
                        <TableCell align="right"><strong>Press Pending</strong></TableCell>
                        <TableCell align="right"><strong>Repair</strong></TableCell>
                        <TableCell align="right"><strong>Sys Total</strong></TableCell>
                        <TableCell align="right"><strong>Variance</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(comparisonResult.comparison || [])
                        .filter((row: any) => !showMismatchOnly || row.has_mismatch)
                        .map((row: any, idx: number) => (
                          <TableRow key={idx} sx={row.has_mismatch ? { bgcolor: '#fff0f0' } : undefined}>
                            <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.article_name}</TableCell>
                            <TableCell><strong>{row.design_no}</strong></TableCell>
                            <TableCell>{row.size}</TableCell>
                            <TableCell align="right"><strong>{row.erp_qty}</strong></TableCell>
                            <TableCell align="right">{row.system_qc_pending}</TableCell>
                            <TableCell align="right">{row.system_press_pending}</TableCell>
                            <TableCell align="right">{row.system_repair_pending}</TableCell>
                            <TableCell align="right"><strong>{row.system_total_pending}</strong></TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: row.variance !== 0 ? 'error.main' : 'success.main' }}>
                              {row.variance > 0 ? '+' : ''}{row.variance}
                            </TableCell>
                          </TableRow>
                        ))}
                      {comparisonResult.comparison?.length === 0 && (
                        <TableRow><TableCell colSpan={9} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No matched data to compare</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Unmatched rows */}
                {comparisonResult.unmatched > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <strong>{comparisonResult.unmatched}</strong> rows could not be matched
                      <Button startIcon={<DownloadIcon />} onClick={downloadComparisonUnmatchedCSV} sx={{ ml: 2 }} size="small">
                        Download Unmatched CSV
                      </Button>
                    </Alert>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Article Name</strong></TableCell>
                            <TableCell><strong>Size</strong></TableCell>
                            <TableCell><strong>Extracted Design No</strong></TableCell>
                            <TableCell><strong>Reason</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {comparisonResult.unmatched_rows.slice(0, 50).map((row: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>{row.article_name || '-'}</TableCell>
                              <TableCell>{row.size || '-'}</TableCell>
                              <TableCell>{row.extracted_design_no || '-'}</TableCell>
                              <TableCell>{row.reason || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {comparisonResult.unmatched_rows.length > 50 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Showing first 50 of {comparisonResult.unmatched_rows.length} unmatched rows. Download CSV to see all.
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Results Dialog */}
      <Dialog
        open={resultsDialogOpen}
        onClose={() => setResultsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Bulk Upload Results
        </DialogTitle>
        <DialogContent>
          {bulkResults && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully processed <strong>{bulkResults.processed}</strong> designs
              </Alert>
              
              {bulkResults.unmatched > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>{bulkResults.unmatched}</strong> rows could not be matched
                  {bulkResults.unmatched_rows && bulkResults.unmatched_rows.length > 0 && (
                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={downloadUnmatchedCSV}
                      sx={{ ml: 2 }}
                      size="small"
                    >
                      Download Unmatched CSV
                    </Button>
                  )}
                </Alert>
              )}

              {bulkResults.processing_errors && bulkResults.processing_errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <strong>{bulkResults.errors}</strong> designs had processing errors. See details below.
                </Alert>
              )}

              {bulkResults.processed_designs && bulkResults.processed_designs.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Processed Designs:
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Design No</strong></TableCell>
                          <TableCell><strong>Batch Number</strong></TableCell>
                          <TableCell align="right"><strong>Total Issued</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkResults.processed_designs.map((design: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{design.design_no}</TableCell>
                            <TableCell>{design.batch_number}</TableCell>
                            <TableCell align="right">{design.total_issued}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {bulkResults.processing_errors && bulkResults.processing_errors.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom color="error">
                    Processing Errors:
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Design No</strong></TableCell>
                          <TableCell><strong>Article Name(s)</strong></TableCell>
                          <TableCell><strong>Sizes</strong></TableCell>
                          <TableCell><strong>Row Count</strong></TableCell>
                          <TableCell><strong>Error Message</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkResults.processing_errors.map((error: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell><strong>{error.design_no || '-'}</strong></TableCell>
                            <TableCell>
                              {error.article_names && Array.isArray(error.article_names) 
                                ? error.article_names.join(', ') 
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {error.sizes && Array.isArray(error.sizes) 
                                ? error.sizes.join(', ') 
                                : '-'}
                            </TableCell>
                            <TableCell>{error.row_count || '-'}</TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color="error" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxWidth: 400
                                }}
                              >
                                {error.reason || error.error_message || 'Unknown error'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {bulkResults.unmatched_rows && bulkResults.unmatched_rows.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Unmatched Rows:
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Article Name</strong></TableCell>
                          <TableCell><strong>Size</strong></TableCell>
                          <TableCell><strong>Repair Type (from CSV)</strong></TableCell>
                          <TableCell><strong>Reason</strong></TableCell>
                          <TableCell><strong>Extracted Design No</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkResults.unmatched_rows.slice(0, 50).map((row: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{row.article_name || row['Article Name'] || '-'}</TableCell>
                            <TableCell>{row.size || row['Size'] || '-'}</TableCell>
                            <TableCell>{row.repair_type_from_csv || row['Repair Type'] || '-'}</TableCell>
                            <TableCell>{row.reason || '-'}</TableCell>
                            <TableCell>{row.extracted_design_no || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {bulkResults.unmatched_rows.length > 50 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Showing first 50 of {bulkResults.unmatched_rows.length} unmatched rows. Download CSV to see all.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsDialogOpen(false)}>
            Close
          </Button>
          {bulkResults && bulkResults.processed > 0 && (
            <Button
              variant="contained"
              onClick={() => {
                setResultsDialogOpen(false);
                navigate('/production/qc');
              }}
            >
              Go to QC Station
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
